/**
 * New Arrivals Slider — Vanilla JS
 * Touch swipe + Navigation Arrows + Dots
 */
class NewArrivalsSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('[data-slider-track]');
    this.slides = container.querySelectorAll('[data-slider-slide]');
    this.prevBtn = container.querySelector('[data-slider-prev]');
    this.nextBtn = container.querySelector('[data-slider-next]');
    this.dotsContainer = container.querySelector('[data-slider-dots]');
    
    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    this.init();
  }

  init() {
    this.setupResponsive();
    this.setupArrows();
    this.setupDots();
    this.setupTouch();
    this.goToSlide(0);
    
    // Recalculate on resize
    window.addEventListener('resize', () => {
      this.setupResponsive();
      this.goToSlide(this.currentIndex);
    });
  }

  getSlidesPerView() {
    const width = window.innerWidth;
    if (width <= 575) {
      return parseInt(this.container.dataset.mobileCols) || 2;
    } else if (width <= 991) {
      return 3;
    } else {
      return parseInt(this.container.dataset.desktopCols) || 4;
    }
  }

  getSlideWidth() {
    if (this.slides.length === 0) return 0;
    const gap = window.innerWidth <= 575 ? 12 : 20;
    const perView = this.getSlidesPerView();
    const viewportWidth = this.track.parentElement.offsetWidth;
    return (viewportWidth - (gap * (perView - 1))) / perView;
  }

  getMaxIndex() {
    const perView = this.getSlidesPerView();
    return Math.max(0, this.slides.length - perView);
  }

  setupResponsive() {
    const perView = this.getSlidesPerView();
    const slideWidth = this.getSlideWidth();
    const gap = window.innerWidth <= 575 ? 12 : 20;

    this.slides.forEach(slide => {
      slide.style.flex = `0 0 ${slideWidth}px`;
      slide.style.marginRight = `${gap}px`;
    });
  }

  setupArrows() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1);
        }
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.currentIndex < this.getMaxIndex()) {
          this.goToSlide(this.currentIndex + 1);
        }
      });
    }
  }

  setupDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    
    const totalDots = this.getMaxIndex() + 1;
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.classList.add('dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    const dots = this.dotsContainer.querySelectorAll('.dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === this.currentIndex);
    });
  }

  updateArrows() {
    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.4' : '1';
      this.prevBtn.style.pointerEvents = this.currentIndex === 0 ? 'none' : 'auto';
    }
    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.currentIndex >= this.getMaxIndex() ? '0.4' : '1';
      this.nextBtn.style.pointerEvents = this.currentIndex >= this.getMaxIndex() ? 'none' : 'auto';
    }
  }

  goToSlide(index) {
    this.currentIndex = Math.min(index, this.getMaxIndex());
    this.currentIndex = Math.max(0, this.currentIndex);
    
    const slideWidth = this.getSlideWidth();
    const gap = window.innerWidth <= 575 ? 12 : 20;
    const offset = this.currentIndex * (slideWidth + gap);
    
    this.track.style.transform = `translateX(-${offset}px)`;
    this.prevTranslate = -offset;
    this.currentTranslate = -offset;

    this.updateDots();
    this.updateArrows();
  }

  // ── Touch / Swipe ──
  setupTouch() {
    this.track.addEventListener('touchstart', (e) => this.touchStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.touchMove(e), { passive: true });
    this.track.addEventListener('touchend', () => this.touchEnd());

    // Mouse drag
    this.track.addEventListener('mousedown', (e) => this.dragStart(e));
    this.track.addEventListener('mousemove', (e) => this.dragMove(e));
    this.track.addEventListener('mouseup', () => this.touchEnd());
    this.track.addEventListener('mouseleave', () => {
      if (this.isDragging) this.touchEnd();
    });
  }

  touchStart(e) {
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.track.style.transition = 'none';
  }

  dragStart(e) {
    this.isDragging = true;
    this.startX = e.clientX;
    this.track.style.transition = 'none';
    e.preventDefault();
  }

  touchMove(e) {
    if (!this.isDragging) return;
    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const diff = currentX - this.startX;
    this.currentTranslate = this.prevTranslate + diff;
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  dragMove(e) {
    if (!this.isDragging) return;
    const currentX = e.clientX;
    const diff = currentX - this.startX;
    this.currentTranslate = this.prevTranslate + diff;
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = this.getSlideWidth() / 4;

    if (movedBy < -threshold && this.currentIndex < this.getMaxIndex()) {
      this.currentIndex++;
    } else if (movedBy > threshold && this.currentIndex > 0) {
      this.currentIndex--;
    }

    this.goToSlide(this.currentIndex);
  }
}

// Initialize all sliders on page
document.querySelectorAll('[data-slider-viewport]').forEach(viewport => {
  const wrapper = viewport.closest('.na-wrapper');
  if (wrapper) {
    // Pass data attributes for config
    const sectionId = wrapper.dataset.sectionId;
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    
    // Read column settings from CSS or data attributes
    // We'll store them on the wrapper for JS access
    const desktopCols = wrapper.querySelector('.na-slider-track')?.dataset?.desktopCols || 4;
    const mobileCols = wrapper.querySelector('.na-slider-track')?.dataset?.mobileCols || 2;
    
    wrapper.dataset.desktopCols = desktopCols;
    wrapper.dataset.mobileCols = mobileCols;
    
    new NewArrivalsSlider(wrapper);
  }
});